/**
 * Format phone number with international formatting
 * @param value - Raw phone number input
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(value: string): string {
  // Keep only digits and + symbol
  let cleaned = value.replace(/[^\d+]/g, '');
  
  // Don't format if empty
  if (!cleaned) return '';
  
  // If it doesn't start with +, add it
  if (!cleaned.startsWith('+')) {
    // If user starts typing without +, assume they want to add country code
    if (cleaned.length > 0) {
      cleaned = '+' + cleaned;
    }
  }
  
  // Extract country code and number parts
  const withoutPlus = cleaned.slice(1); // Remove the + for processing
  
  if (withoutPlus.length === 0) {
    return '+';
  }
  
  // Format based on length and common international patterns
  if (withoutPlus.length <= 3) {
    // Just country code
    return `+${withoutPlus}`;
  } else if (withoutPlus.length <= 6) {
    // Country code + partial number
    return `+${withoutPlus.slice(0, 3)} ${withoutPlus.slice(3)}`;
  } else if (withoutPlus.length <= 10) {
    // Country code + formatted number
    const countryCode = withoutPlus.slice(0, 2);
    const remaining = withoutPlus.slice(2);
    
    // Format the remaining digits in groups
    if (remaining.length <= 3) {
      return `+${countryCode} ${remaining}`;
    } else if (remaining.length <= 6) {
      return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3)}`;
    } else {
      return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3, 6)} ${remaining.slice(6)}`;
    }
  } else {
    // Longer international numbers
    const countryCode = withoutPlus.slice(0, 3);
    const remaining = withoutPlus.slice(3);
    
    // Format in groups of 3-4 digits
    if (remaining.length <= 3) {
      return `+${countryCode} ${remaining}`;
    } else if (remaining.length <= 6) {
      return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3)}`;
    } else if (remaining.length <= 9) {
      return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3, 6)} ${remaining.slice(6)}`;
    } else {
      return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3, 6)} ${remaining.slice(6, 10)} ${remaining.slice(10)}`;
    }
  }
}

/**
 * Validate phone number format
 * @param phone - Phone number to validate
 * @returns True if valid format
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone) return true; // Optional field
  
  // Remove all non-digit characters for validation
  const numbers = phone.replace(/\D/g, '');
  
  // International format should have country code + national number
  // Minimum: country code (1-3 digits) + national number (4+ digits) = 5+ total
  // Maximum: 15 digits total (ITU-T E.164 standard)
  if (numbers.length < 5 || numbers.length > 15) {
    return false;
  }
  
  // Must start with + when formatted
  const formatted = phone.trim();
  if (formatted.length > 0 && !formatted.startsWith('+')) {
    return false;
  }
  
  return true;
}

/**
 * Clean phone number for storage (remove formatting)
 * @param phone - Formatted phone number
 * @returns Clean phone number for database storage in E.164 format
 */
export function cleanPhoneNumber(phone: string): string {
  if (!phone) return '';
  
  // Keep only digits and + for international format
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Ensure it starts with + for international format
  if (cleaned && !cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  
  return cleaned;
} 